import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Input,
  Select,
  HStack,
  Button,
  Flex,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  SimpleGrid,
  Grid,
  GridItem,
  Icon,
  InputGroup,
  InputLeftElement,
  Badge,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Divider
} from '@chakra-ui/react';

import { 
  ChevronUpIcon, 
  ChevronDownIcon 
} from '@chakra-ui/icons';

import { 
  MdFilterList, 
  MdRefresh, 
  MdSearch, 
  MdDownload, 
  MdClear,
  MdWarning
} from 'react-icons/md';

const API_MSSE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const PrecatoriosPendentes = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  // Search & filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNatureza, setFilterNatureza] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [currentPriceRange, setCurrentPriceRange] = useState([0, 0]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const highlightColor = useColorModeValue('brand.primary', 'brand.secondary');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    const fetchPrecatorios = async () => {
      try {
        // Here we would typically fetch data from a different endpoint
        const res = await fetch(`${API_MSSE_URL}/precatorioMS`);
        // For now, using the same data as the main page, but filtering for "pending" ones
        // In a real application, this would be replaced with actual filtered data from the API
        const data = await res.json();
        // For demo purposes, filtering to create a subset that represents "pending" precatorios
        const pendingData = data.filter((p, index) => index % 3 === 1); // Just a sample filter
        setPrecatorios(pendingData);
        
        // Initialize price range based on data
        if (pendingData.length > 0) {
          const values = pendingData.map(p => p.valor_atualizado);
          const minValue = Math.max(0, Math.floor(Math.min(...values)));
          const maxValue = Math.ceil(Math.max(...values));
          setPriceRange([minValue, maxValue]);
          setCurrentPriceRange([minValue, maxValue]);
        }
      } catch (err) {
        console.error(err);
        setErro(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPrecatorios();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ isActive, direction }) => {
    return direction === 'asc' ? (
      <ChevronUpIcon ml={2} opacity={isActive ? 1 : 0.3} />
    ) : (
      <ChevronDownIcon ml={2} opacity={isActive ? 1 : 0.3} />
    );
  };

  // Extract unique filters from data
  const naturezas = useMemo(() => [...new Set(precatorios.map(p => p.natureza).filter(Boolean))], [precatorios]);
  const entes = useMemo(() => [...new Set(precatorios.map(p => p.ente).filter(Boolean))], [precatorios]);

  const filteredData = useMemo(() => {
    let result = [...precatorios];
  
    // Search + Filters
    result = result.filter(p =>
      p.numero_precatorio.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (!filterNatureza || p.natureza === filterNatureza) &&
      (!filterEnte || p.ente === filterEnte) &&
      p.valor_atualizado >= currentPriceRange[0] &&
      p.valor_atualizado <= currentPriceRange[1]
    );
  
    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
    
        // Handle 'recebimento' as a date
        if (sortConfig.key === 'recebimento') {
          const aDate = new Date(aVal.split('/').reverse().join('-'));
          const bDate = new Date(bVal.split('/').reverse().join('-'));
    
          return sortConfig.direction === 'asc'
            ? aDate - bDate
            : bDate - aDate;
        }
    
        // Handle numeric values
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
    
        // Fallback: string comparison
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
    
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [precatorios, searchTerm, filterNatureza, filterEnte, currentPriceRange, sortConfig]);
  
  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  // Handlers
  const handlePageChange = (direction) => {
    setCurrentPage(prev => {
      if (direction === 'next' && prev < totalPages) return prev + 1;
      if (direction === 'prev' && prev > 1) return prev - 1;
      return prev;
    });
  };

  const handlePriceRangeChange = (values) => {
    // Ensure minimum is not negative
    const minValue = Math.max(0, values[0]);
    // Ensure maximum is not less than minimum
    const maxValue = Math.max(minValue, values[1]);
    setCurrentPriceRange([minValue, maxValue]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterNatureza('');
    setFilterEnte('');
    setCurrentPriceRange([priceRange[0], priceRange[1]]);
    setCurrentPage(1);
  };

  const formatCurrency = (value) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  // Calculate total value
  const totalValue = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + item.valor_atualizado, 0);
  }, [filteredData]);

  return (
    <Box p={{ base: 4, md: 8 }}>
      {/* Header Section */}
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        justify="space-between" 
        align={{ base: 'flex-start', md: 'center' }} 
        mb={8}
      >
        <Box mb={{ base: 4, md: 0 }}>
          <Heading 
            fontSize={{ base: '2xl', md: '3xl' }} 
            fontWeight="bold"
            color={headingColor}
            mb={2}
          >
            Precatórios Pendentes-Potenciais – TJ-MS
          </Heading>
          <Text color={textColor}>
            Lista de Precatórios Pendentes com Potencial do Tribunal de Justiça do Maranhão
          </Text>
        </Box>
        <HStack spacing={3}>
          <Button 
            leftIcon={<Icon as={MdRefresh} />}
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Atualizar
          </Button>
          <Button 
            leftIcon={<Icon as={MdDownload} />}
            variant="solid"
            colorScheme="blue"
            size="sm"
          >
            Exportar
          </Button>
        </HStack>
      </Flex>

      {loading ? (
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody py={12} textAlign="center">
            <VStack spacing={4}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text>Carregando dados de precatórios...</Text>
            </VStack>
          </CardBody>
        </Card>
      ) : erro ? (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          Erro ao buscar os dados de precatórios. Tente novamente mais tarde.
        </Alert>
      ) : (
        <>
          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Total de Precatórios</StatLabel>
                  <StatNumber>{totalItems}</StatNumber>
                  <StatHelpText>{totalItems === 1 ? 'Precatório pendente' : 'Precatórios pendentes'}</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Valor Total</StatLabel>
                  <StatNumber>{formatCurrency(totalValue)}</StatNumber>
                  <StatHelpText>Soma dos valores atualizados</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Valor Médio</StatLabel>
                  <StatNumber>
                    {totalItems > 0 
                      ? formatCurrency(totalValue / totalItems) 
                      : 'R$ 0,00'}
                  </StatNumber>
                  <StatHelpText>Por precatório</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Filters Card */}
          <Card 
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
            overflow="hidden"
            border="1px"
            borderColor={borderColor}
            mb={6}
          >
            <CardBody py={4}>
              <Grid 
                templateColumns={{ base: "1fr", md: "1fr 1fr auto auto" }}
                gap={4}
                alignItems="center"
              >
                <GridItem>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input 
                      placeholder="Buscar por nº precatório" 
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </InputGroup>
                </GridItem>
                
                <GridItem>
                  <HStack>
                    <Select 
                      placeholder="Filtrar por natureza"
                      value={filterNatureza}
                      onChange={(e) => {
                        setFilterNatureza(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      {naturezas.map((natureza) => (
                        <option key={natureza} value={natureza}>
                          {natureza}
                        </option>
                      ))}
                    </Select>
                    
                    <Select 
                      placeholder="Filtrar por ente"
                      value={filterEnte}
                      onChange={(e) => {
                        setFilterEnte(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      {entes.map((ente) => (
                        <option key={ente} value={ente}>
                          {ente}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                </GridItem>
                
                <GridItem>
                  <Button 
                    leftIcon={<Icon as={MdFilterList} />}
                    onClick={onOpen}
                    size="md"
                    variant="outline"
                    w="full"
                  >
                    Filtros Avançados
                  </Button>
                </GridItem>
                
                <GridItem>
                  <HStack justifyContent="flex-end">
                    <Select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      size="md"
                    >
                      {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option} por página
                        </option>
                      ))}
                    </Select>
                    
                    {(searchTerm || filterNatureza || filterEnte || 
                      currentPriceRange[0] !== priceRange[0] || 
                      currentPriceRange[1] !== priceRange[1]) && (
                      <Button 
                        leftIcon={<Icon as={MdClear} />}
                        onClick={clearFilters}
                        size="md"
                        variant="ghost"
                        colorScheme="red"
                      >
                        Limpar
                      </Button>
                    )}
                  </HStack>
                </GridItem>
              </Grid>

              {/* Price Range Drawer */}
              <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
                <DrawerOverlay />
                <DrawerContent>
                  <DrawerCloseButton />
                  <DrawerHeader borderBottomWidth="1px">
                    Filtros Avançados
                  </DrawerHeader>

                  <DrawerBody>
                    <VStack spacing={6} align="stretch" pt={4}>
                      <FormControl>
                        <FormLabel fontWeight="medium">
                          Faixa de Valor (R$)
                        </FormLabel>
                        
                        <Flex justifyContent="space-between" alignItems="center" mb={4}>
                          <Input 
                            type="text" 
                            value={`R$ ${currentPriceRange[0].toLocaleString('pt-BR')}`}
                            onChange={(e) => {
                              const value = Number(e.target.value.replace(/[^0-9]/g, ''));
                              if (!isNaN(value)) {
                                setCurrentPriceRange([value, currentPriceRange[1]]);
                              }
                            }}
                            bg="gray.700"
                            color="white"
                            w="140px"
                          />
                          
                          <Input 
                            type="text" 
                            value={`R$ ${currentPriceRange[1].toLocaleString('pt-BR')}`}
                            onChange={(e) => {
                              const value = Number(e.target.value.replace(/[^0-9]/g, ''));
                              if (!isNaN(value)) {
                                setCurrentPriceRange([currentPriceRange[0], value]);
                              }
                            }}
                            w="140px"
                          />
                        </Flex>
                        
                        <RangeSlider
                          min={priceRange[0]}
                          max={priceRange[1]}
                          step={(priceRange[1] - priceRange[0]) / 100}
                          value={currentPriceRange}
                          onChange={handlePriceRangeChange}
                          colorScheme="blue"
                        >
                          <RangeSliderTrack>
                            <RangeSliderFilledTrack />
                          </RangeSliderTrack>
                          <RangeSliderThumb index={0} />
                          <RangeSliderThumb index={1} />
                        </RangeSlider>
                      </FormControl>

                      <Divider />

                      <Box>
                        <Text fontWeight="medium" mb={2}>Filtros Aplicados:</Text>
                        <VStack align="start" spacing={2}>
                          {searchTerm && (
                            <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                              Busca: {searchTerm}
                            </Badge>
                          )}
                          {filterNatureza && (
                            <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                              Natureza: {filterNatureza}
                            </Badge>
                          )}
                          {filterEnte && (
                            <Badge colorScheme="purple" px={2} py={1} borderRadius="full">
                              Ente: {filterEnte}
                            </Badge>
                          )}
                          {(currentPriceRange[0] !== priceRange[0] || currentPriceRange[1] !== priceRange[1]) && (
                            <Badge colorScheme="orange" px={2} py={1} borderRadius="full">
                              Valor: {formatCurrency(currentPriceRange[0])} - {formatCurrency(currentPriceRange[1])}
                            </Badge>
                          )}
                        </VStack>
                      </Box>
                    </VStack>
                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={clearFilters}>
                      Limpar Filtros
                    </Button>
                    <Button colorScheme="blue" onClick={onClose}>
                      Aplicar
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </CardBody>
          </Card>

          {/* Results Card */}
          <Card 
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
            overflow="hidden"
            border="1px"
            borderColor={borderColor}
            mb={6}
          >
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={headingColor}>
                  Precatórios Pendentes-Potenciais
                </Heading>
                <Badge colorScheme="yellow" fontSize="sm" py="1" px="2" borderRadius="md">
                  {totalItems} {totalItems === 1 ? 'resultado' : 'resultados'}
                </Badge>
              </Flex>
            </CardHeader>
            
            <CardBody p={0}>
              <Box overflowX="auto" w="100%">
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th onClick={() => handleSort('ordem')} cursor="pointer">
                        <Flex align="center">
                          Ordem
                          <SortIcon
                            isActive={sortConfig.key === 'ordem'}
                            direction={sortConfig.key === 'ordem' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('numero_precatorio')} cursor="pointer">
                        <Flex align="center">
                          Nº Precatório
                          <SortIcon
                            isActive={sortConfig.key === 'numero_precatorio'}
                            direction={sortConfig.key === 'numero_precatorio' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('natureza')} cursor="pointer">
                        <Flex align="center">
                          Natureza
                          <SortIcon
                            isActive={sortConfig.key === 'natureza'}
                            direction={sortConfig.key === 'natureza' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('orcamento')} cursor="pointer">
                        <Flex align="center">
                          Orçamento
                          <SortIcon
                            isActive={sortConfig.key === 'orcamento'}
                            direction={sortConfig.key === 'orcamento' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('recebimento')} cursor="pointer">
                        <Flex align="center">
                          Recebimento
                          <SortIcon
                            isActive={sortConfig.key === 'recebimento'}
                            direction={sortConfig.key === 'recebimento' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('prioridade')} cursor="pointer">
                        <Flex align="center">
                          Prioridade
                          <SortIcon
                            isActive={sortConfig.key === 'prioridade'}
                            direction={sortConfig.key === 'prioridade' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('valor_atualizado')} cursor="pointer">
                        <Flex align="center">
                          Valor Atualizado
                          <SortIcon
                            isActive={sortConfig.key === 'valor_atualizado'}
                            direction={sortConfig.key === 'valor_atualizado' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('ente')} cursor="pointer">
                        <Flex align="center">
                          Ente
                          <SortIcon
                            isActive={sortConfig.key === 'ente'}
                            direction={sortConfig.key === 'ente' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((item) => (
                        <Tr 
                          key={item.numero_precatorio}
                          _hover={{ bg: hoverBg }}
                          transition="background-color 0.2s"
                        >
                          <Td>{item.ordem}</Td>
                          <Td fontWeight="medium" color={highlightColor}>{item.numero_precatorio}</Td>
                          <Td>
                            <Badge colorScheme={item.natureza === 'ALIMENTAR' ? 'green' : 'purple'} variant="subtle">
                              {item.natureza}
                            </Badge>
                          </Td>
                          <Td>{item.orcamento}</Td>
                          <Td>{item.recebimento}</Td>
                          <Td>{item.prioridade || '-'}</Td>
                          <Td fontWeight="medium">R$ {item.valor_atualizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Td>
                          <Td>{item.ente}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={8} textAlign="center" py={8}>
                          <VStack spacing={3}>
                            <Text color={textColor}>Nenhum precatório encontrado com os filtros selecionados</Text>
                            <Button 
                              size="sm"
                              variant="outline"
                              leftIcon={<Icon as={MdClear} />}
                              onClick={clearFilters}
                            >
                              Limpar Filtros
                            </Button>
                          </VStack>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>

            {paginatedData.length > 0 && (
              <CardFooter borderTopWidth="1px" borderColor={borderColor} justifyContent="space-between">
                <Text fontSize="sm" color={textColor}>
                  Página {currentPage} de {totalPages} ({totalItems} registros)
                </Text>
                <HStack>
                  <Button 
                    onClick={() => handlePageChange('prev')} 
                    isDisabled={currentPage === 1}
                    size="sm"
                    variant="outline"
                  >
                    Anterior
                  </Button>
                  <Button 
                    onClick={() => handlePageChange('next')} 
                    isDisabled={currentPage === totalPages}
                    size="sm"
                    variant="solid"
                    colorScheme="blue"
                  >
                    Próxima
                  </Button>
                </HStack>
              </CardFooter>
            )}
          </Card>

          {/* Info Footer */}
          <Flex 
            mt={10} 
            justify="space-between" 
            fontSize="xs" 
            color={textColor}
            borderTop="1px"
            borderColor={borderColor}
            pt={4}
          >
            <Text>Fonte: API TJ-MS | Última atualização: Hoje, 09:45</Text>
            <Text>
              Em caso de dúvidas, entre em contato com o suporte técnico
            </Text>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default PrecatoriosPendentes;